/* eslint-disable @typescript-eslint/no-explicit-any */
import { SSOApi } from '@demand-iq/auth-sdk';
import {
  AEOAPIApi,
  ApiKeyAPIApi,
  CalendarAPIApi,
  CompanyAPIApi,
  EmbedCodeAPIApi,
  Middleware,
  OrganizationAPIApi,
  PlanAPIApi,
  PolicyAPIApi,
  PublicAPIApi,
  RequestContext,
  ResponseContext,
  RoleAPIApi,
  ServerConfiguration,
  SettingsAPIApi,
  TeamAPIApi,
  TerritoryAPIApi,
  UserAPIApi,
  UserAPIV2Api,
  createConfiguration,
} from '@demand-iq/user-sdk';
import { useToken } from 'hooks/useToken';
import handlePostAuth, { applyRequestAuth, clientConfig } from './authUtils';

const routesRequireUserToken = ['/admin/user/me', '/admin/plans'];

/**
 * Routes that must resolve against the admin's own identity even while they are switched
 * into a company. `/profile/details` is a one-off: on that page the admin is looking at
 * their own profile, so the user detail route has to use their own token there and the
 * spoof token everywhere else.
 */
export const requiresUserToken = (url: string): boolean => {
  if (routesRequireUserToken.some((r) => url.includes(r))) {
    return true;
  }
  return window.location.pathname === '/profile/details' && url.includes('/admin/user/');
};

const variable = {};
const server = new ServerConfiguration(clientConfig.url, variable);

// Convert to actual configuration
export type UserApi = Readonly<{
  user: UserAPIApi;
  userV2: UserAPIV2Api;
  roles: RoleAPIApi;
  company: CompanyAPIApi;
  forgot: PublicAPIApi;
  apiKey: ApiKeyAPIApi;
  policies: PolicyAPIApi;
  teams: TeamAPIApi;
  settings: SettingsAPIApi;
  sso: SSOApi;
  territory: TerritoryAPIApi;
  calendar: CalendarAPIApi;
  plan: PlanAPIApi;
  organization: OrganizationAPIApi;
  embedCode: EmbedCodeAPIApi;
  aeo: AEOAPIApi;
}>;

const useAuthenticatedUserClient = (): UserApi => {
  const { addTokenForClientId } = useToken();

  const authCheck: Middleware = {
    pre(context: RequestContext): Promise<RequestContext> {
      applyRequestAuth(context, requiresUserToken(context.getUrl()));
      return Promise.resolve(context);
    },

    post(context: ResponseContext): Promise<ResponseContext> {
      return handlePostAuth(context, addTokenForClientId);
    },
  };

  const config = createConfiguration({
    baseServer: server,
    promiseMiddleware: [authCheck],
  });
  const forgetPasswordConfig = createConfiguration({
    baseServer: server,
  });

  return {
    user: new UserAPIApi(config),
    userV2: new UserAPIV2Api(config),
    roles: new RoleAPIApi(config),
    company: new CompanyAPIApi(config),
    forgot: new PublicAPIApi(forgetPasswordConfig),
    apiKey: new ApiKeyAPIApi(config),
    policies: new PolicyAPIApi(config),
    teams: new TeamAPIApi(config),
    settings: new SettingsAPIApi(config),
    sso: new SSOApi(config as any),
    territory: new TerritoryAPIApi(config),
    calendar: new CalendarAPIApi(config),
    plan: new PlanAPIApi(config),
    organization: new OrganizationAPIApi(config),
    embedCode: new EmbedCodeAPIApi(config),
    aeo: new AEOAPIApi(config),
  };
};

export default useAuthenticatedUserClient;
